import { offsetPolyline, snapEndpoints } from "./polyline.mjs";

// Center-to-center separation between an opposite-direction pair, in screen pixels.
// The handler bakes a unitless signed `overlap-offset` (±0.5 for a pair: each arc
// shifts 0.5 × this many px to its own side, so the pair ends up this many px
// apart); the style scales by the map resolution so separation is constant across
// zoom. What the eye reads is the gap between the COLORED strokes (5px normal, 7px
// hover/click) — the 9px white casing all but vanishes against the light basemap —
// so tune against the colored width, not the casing: this leaves the colored lines
// ~2px apart at rest (snugly side by side), just shy of touching on hover.
export const OVERLAP_OFFSET_PX = 7;

/**
 * Offset `coords` for display if `feature` was tagged as overlapping; else
 * return `coords` unchanged. Shared by the normal style and the hover circle.
 */
export function applyOverlapOffset(coords, feature, resolution) {
  const overlapOffset = feature.get("overlap-offset");
  if (typeof overlapOffset !== "number" || !resolution) return coords;
  // Only transform the full arc polyline — never a short probe such as the hover
  // tangent (a 2-point line near the cursor that copies the source arc's props,
  // including the node tags). Snapping that to the far-away junction nodes would
  // blow up the hover curvature circle. Every real arc has many vertices — curved
  // ones are sampled densely and straight ones are densified at construction
  // (see STRAIGHT_ARC_POINTS in curvatureArcGeometry.js) precisely so this offset
  // applies to them too.
  if (coords.length < 3) return coords;
  // Pull the arc onto the group's shared junction nodes (its raw endpoints drift
  // ~0.5m from its pair partner's, so this keeps the pair symmetric about a common
  // centerline), then shift it sideways by a constant parallel offset so the pair
  // runs side by side the whole length.
  const startNode = feature.get("overlap-start-node");
  const endNode = feature.get("overlap-end-node");
  const snapped =
    startNode && endNode ? snapEndpoints(coords, startNode, endNode) : coords;
  return offsetPolyline(
    snapped,
    overlapOffset * OVERLAP_OFFSET_PX * resolution,
  );
}
